// Phase B's bodies (gate B5 and the hit reactions): the action-pose layer, hit reactions and ragdolls on @rpg/figure
// and @rpg/collision. `createBodies` (module.ts) is the kernel module any scene adds (the valley at the merge); the
// ragdoll lab (`?scene=ragdoll`, lab.ts and view.ts) is this stream's proving ground. main.ts joins `bodiesFaults`
// into the spike's faults record; src/sim mounts the lab through `mountRagdollLab`.
import * as THREE from "three";
import { finite, type Invariant } from "@rpg/harness";
import { POSE_FAULTS, RAGDOLL_FAULTS, partLowest, ragdollLimitExcess } from "@rpg/figure";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { CombatHit, GameEvents, Vec3 } from "../core";
import type { FaultGroup } from "../faults";
import { createRagdollLab, type RagdollLab } from "./lab";
import type { CollisionWorld } from "@rpg/collision";
import type { Bodies } from "./module";
import { mountRagdollView, type Framing, type RagdollView3 } from "./view";

export { createBodies, bodiesTables, BODIES_C, type Bodies, type BodyLookup, type BodiesState } from "./module";
export { createRagdollLab, type RagdollLab } from "./lab";

/** The bodies' planted defects, as `?faults=` and `__rpg.faults()` take them. */
export const BODIES_FAULT_NAMES = ["oneLimitRemoved", "poseMaskIgnored", "reactionUndirected"] as const;

/** The group for src/faults.ts's joined record (the switches live beside what they break, in @rpg/figure). */
export const bodiesFaults: FaultGroup = {
  names: BODIES_FAULT_NAMES,
  get: (n) => (n === "oneLimitRemoved" ? RAGDOLL_FAULTS.oneLimitRemoved : n === "poseMaskIgnored" ? POSE_FAULTS.maskIgnored : n === "reactionUndirected" ? POSE_FAULTS.undirected : false),
  set: (n, on) => {
    if (n === "oneLimitRemoved") RAGDOLL_FAULTS.oneLimitRemoved = on;
    if (n === "poseMaskIgnored") POSE_FAULTS.maskIgnored = on;
    if (n === "reactionUndirected") POSE_FAULTS.undirected = on;
  },
};

/** Turn on the bodies' defects `?faults=` names. */
export function applyBodiesFaultSwitches(search = typeof location !== "undefined" ? location.search : ""): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of BODIES_FAULT_NAMES) if (on.includes(n)) bodiesFaults.set(n, true);
}

/** How far under the ground a part's lowest point may go (m) and past its limits a joint may turn (rad) before an
 *  invariant trips: Rapier's contacts and joints are soft by a little (the agent's tolerances, pending the user). */
export const RAGDOLL_TOLERANCE = { below: 0.04, limit: 0.2 };

/** The ragdoll lab's scene for the sim host (`?scene=ragdoll`). */
export function ragdollSceneFor(content: SpikeContent): RagdollLab {
  applyBodiesFaultSwitches();
  return createRagdollLab(content);
}

/** A hit to script: the target stand, and what combat would say (defaults: a light hit from the next stand). */
export interface ScriptedHit {
  target: number;
  attacker?: number;
  kind?: CombatHit["kind"];
  severity?: number;
  outcome?: CombatHit["outcome"];
  /** Unit, attacker to target (default: from the attacker stand to the target). */
  dir?: Vec3;
  move?: string;
}

/** Mount the lab's view, handle and invariants once the host has set it up. */
export function mountRagdollLab(o: { lab: RagdollLab; runtime: Runtime; renderer?: THREE.WebGLRenderer; scene: THREE.Object3D; camera?: THREE.PerspectiveCamera; content: SpikeContent; tier: string }) {
  const { lab } = o;
  const view: RagdollView3 | null = o.renderer && o.camera && o.scene instanceof THREE.Scene ? mountRagdollView({ runtime: o.runtime, renderer: o.renderer, scene: o.scene, camera: o.camera, content: o.content, lab, tier: o.tier }) : null;
  const stand = (i: number) => {
    const s = lab.state().stands[i];
    if (!s) throw new Error(`ragdoll lab: no stand ${i} (there are ${lab.state().stands.length})`);
    return s;
  };

  // the current step's worst part under the ground and joint past its limits (the invariants, after every step)
  const now = ragdollWorst(() => lab.bodies(), () => lab.collision(), (x, z) => lab.groundAt(x, z));
  const invariants: Invariant[] = [finite("ragdoll lab stands", () => lab.state().stands.flatMap((s) => [s.x, s.y, s.z, s.yaw])), ...ragdollInvariants(() => lab.bodies(), () => lab.collision(), (x, z) => lab.groundAt(x, z))];

  const handle = {
    /** The soak's numbers so far (every ragdoll measured each step). */
    report: () => ({ ...lab.report(), view: view?.report() ?? null }),
    /** Start a fresh measurement. */
    resetReport: () => lab.resetReport(),
    /** Turn the seeded defeat emitter on or off. */
    auto: (on: boolean) => lab.setAuto(on),
    /** The lab's state: time, the emitter, every stand, the live ragdolls. */
    state: () => ({ ...structuredClone(lab.state()), ragdolls: structuredClone(lab.bodies().state().ragdolls), t: lab.bodies().state().t }),
    /** Script a defeat of stand `i` (applied at the next step). */
    defeat: (i: number, d: { dir: Vec3; severity: number; point?: Vec3 }) => {
      const s = stand(i);
      const m = Math.hypot(...d.dir) || 1;
      lab.script({ kind: "defeat", d: { entity: s.entity, by: 0, point: d.point ?? lab.chest(s), dir: [d.dir[0] / m, d.dir[1] / m, d.dir[2] / m], severity: d.severity } });
    },
    /** Script a `combat:hit` on stand `target` (applied at the next step). */
    hit: (h: ScriptedHit) => {
      const t = stand(h.target);
      const a = stand(h.attacker ?? (h.target + 1) % lab.state().stands.length);
      let dir = h.dir ?? [t.x - a.x, 0, t.z - a.z];
      const m = Math.hypot(...dir) || 1;
      dir = [dir[0] / m, dir[1] / m, dir[2] / m];
      const hit: CombatHit = { attacker: a.entity, target: t.entity, move: h.move ?? "test.light", kind: h.kind ?? "light", severity: h.severity ?? 0.5, damage: 1, point: lab.chest(t), dir, outcome: h.outcome ?? "hit", unblockable: false, crit: false };
      lab.script({ kind: "hit", hit });
    },
    /** Script a `combat:move` phase on stand `i` (applied at the next step). */
    move: (i: number, move: string, phase: GameEvents["combat:move"]["phase"], weapon: string | null = null) => lab.script({ kind: "move", m: { entity: stand(i).entity, move, phase, weapon } }),
    /** Stand `i`'s pose track sampled at the sim's time: the poses playing, their weights and beats. */
    sample: (i: number) => {
      const s = stand(i);
      return lab.bodies().sample(s.entity, s.card, lab.bodies().state().t).active;
    },
    /** The pose a combat move plays. */
    poseForMove: (move: string) => lab.bodies().poseForMove(move),
    /** Stand `i`'s figure as drawn (joint turns, world ups, the pivot, the root). */
    pose: (i: number) => view?.pose(i) ?? null,
    /** Frame the camera: the overview, a stand, or a point. */
    frame: (f: Framing) => view?.frame(f),
    /** Every live ragdoll's parts: where each body's centre is now. */
    parts: () =>
      lab.bodies().state().ragdolls.map((r) => ({
        id: r.state.id,
        card: r.card,
        phase: r.state.phase,
        parts: Object.fromEntries(
          lab.bodies().resolved(r.card)!.parts.map((p, i) => {
            const b = r.state.bodies[i]!;
            const at = lab.collision().has(b) ? lab.collision().pose(b).position : null;
            return [p.id, at ? ([at.x, at.y, at.z] as Vec3) : null];
          }),
        ),
      })),
    /** The ground's height under (x, z). */
    groundAt: (x: number, z: number) => lab.groundAt(x, z),
  };

  return {
    view,
    render: view ? () => view.render() : null,
    handle,
    invariants,
    plantNaN: () => void (lab.state().stands[0]!.x = Number.NaN),
    state: () => {
      const L = lab.state();
      const B = lab.bodies().state();
      return { stands: L.stands.filter((s) => s.alive).length, ragdolls: B.ragdolls.length, defeats: L.defeats, t: B.t, worst: now() };
    },
  };
}

/** The current step's worst ragdoll part under the ground and joint past its part list's limits. */
export function ragdollWorst(bodies: () => Bodies, collision: () => CollisionWorld, groundAt: (x: number, z: number) => number): () => { below: { depth: number; at: string }; limit: { excess: number; at: string } } {
  return () => {
    const B = bodies().state();
    const world = collision();
    let below = { depth: -1, at: "" };
    let limit = { excess: 0, at: "" };
    for (const r of B.ragdolls) {
      const rd = bodies().resolved(r.card);
      if (!rd || r.state.phase === "gone") continue;
      rd.parts.forEach((p, i) => {
        const b = r.state.bodies[i]!;
        if (!world.has(b)) return;
        const low = partLowest(p, world.pose(b));
        const d = groundAt(low.x, low.z) - low.y;
        if (d > below.depth) below = { depth: d, at: `${r.card}/${p.id}` };
      });
      const e = ragdollLimitExcess(world, r.state, rd);
      if (e.excess > limit.excess) limit = { excess: e.excess, at: `${r.card}/${e.part}` };
    }
    return { below, limit };
  };
}

/** A scene's ragdoll checks, after every step (the lab's and the fights'): no part's lowest point more than
 *  `RAGDOLL_TOLERANCE.below` under the ground, no joint more than `RAGDOLL_TOLERANCE.limit` past its part list's limits. */
export function ragdollInvariants(bodies: () => Bodies, collision: () => CollisionWorld, groundAt: (x: number, z: number) => number): Invariant[] {
  const now = ragdollWorst(bodies, collision, groundAt);
  return [
    () => {
      const b = now().below;
      return b.depth > RAGDOLL_TOLERANCE.below ? `ragdoll part below the ground (${b.at})` : null;
    },
    () => {
      const l = now().limit;
      return l.excess > RAGDOLL_TOLERANCE.limit ? `ragdoll joint past its limit (${l.at})` : null;
    },
  ];
}
